import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatRadioChange } from "@angular/material/radio";
import { Subject, takeUntil } from "rxjs";
import {
	PAYMENT_EWALLET_TYPE,
	PAYMENT_OPTION_ATOME,
	PAYMENT_OPTION_BNPL,
	PAYMENT_OPTION_EPP,
	PAYMENT_OPTION_EPP_3_6_12_MONTHS,
	PAYMENT_OPTION_EPP_6_12_MONTHS,
	PAYMENT_OPTION_EWALLET,
	PAYMENT_OPTION_FP,
	PAYMENT_OPTION_FPX,
	PAYMENT_OPTION_FPX_ENTERPRISE,
	PAYMENT_FPX_ENTERPRISE_TYPE,
	PAYMENT_OPTION_PAYEX,
	PAYMENT_OPTION_RAZER,
	PAYMENT_OPTION_SHOPBACK,
	PAYMENT_OPTION_SHOPEE,
	PAYMENT_OPTION_VISA
} from "src/app/share/constants/payment-options";
import {
	IPaymentData,
	IPaymentValues,
} from "src/app/share/interface/payment.interface";
import { OrderDataService } from "../../data-service/order-data.service";

@Component({
	selector: "app-payment-options",
	templateUrl: "./payment-options.component.html",
	styleUrls: ["./payment-options.component.scss"],
})
export class PaymentOptionsComponent implements OnInit {
	paymentOptionShopback: string = PAYMENT_OPTION_SHOPBACK;
	paymentOptionFullPrice: string = PAYMENT_OPTION_FP;
	paymentOptionShopee: string = PAYMENT_OPTION_SHOPEE;
	paymentOptionRazer: string = PAYMENT_OPTION_RAZER;
	paymentOptionEPP: string = PAYMENT_OPTION_EPP;
	paymentOptionPayex: string = PAYMENT_OPTION_PAYEX;
	paymentOptionEPP3Or6Or12Months: string = PAYMENT_OPTION_EPP_3_6_12_MONTHS;
	paymentOptionEPP6Or12Months: string = PAYMENT_OPTION_EPP_6_12_MONTHS;
	paymentOptionAtome: string = PAYMENT_OPTION_ATOME;
	paymentOptionBNPL: string = PAYMENT_OPTION_BNPL;

	paymentOptionVisa: string = PAYMENT_OPTION_VISA;
	paymentOptionEwallet: string = PAYMENT_OPTION_EWALLET;
	paymentOptionFpx: string = PAYMENT_OPTION_FPX;
	paymentOptionFpxEnterprise: string = PAYMENT_OPTION_FPX_ENTERPRISE;

	ewalletTypeListing: any[] = PAYMENT_EWALLET_TYPE;
	fpxEnterpriseTypeListing: any[] = PAYMENT_FPX_ENTERPRISE_TYPE;
	epp3Or6Or12MonthsListing: any[] = [];
	epp6Or12MonthsListing: any[] = [];
	epp3Or6Or12MonthsBanks: any[] = [];
	epp6Or12MonthsBanks: any[] = [];
	installmentOptions: any[] = [];
	selectedEppRate: number = 0;

	totalPriceAfterCharge: number;
	paymentFormGroup: UntypedFormGroup;
	paymentType: string;

	showEPP3Or6Or12Months: boolean = false;
	showEPP6Or12Months: boolean = false;

	private shopeePay = "ShopeePay";

	private destroy$ = new Subject();

	@Input() paymentData: IPaymentData;
	@Input() promo: any;
	@Input() paymentValues: IPaymentValues;
	@Input() isApplyDraft: boolean;
	@Input() request: any;
	@Output() selectedPaymentData = new EventEmitter<IPaymentData>();

	constructor(private orderDataService: OrderDataService) {}

	ngOnInit(): void {
		this.initPaymentFormGroup();
		this.setEppBanksList();

		// // Listen for changes in paymentTypeVal
		// this.paymentTypeVal.valueChanges.subscribe((newPaymentType) => {
		// 	// If the new payment type(means choosing Shopback, EPP etc.) is not full price, reset paymentChannel
		// 	if (newPaymentType !== this.paymentOptionFullPrice) {
		// 	  this.paymentFormGroup?.get('paymentChannel').reset(null);
		// 	}
		//   });
	}

	private initPaymentFormGroup() {
		this.paymentFormGroup = new UntypedFormGroup({
			paymentType: new UntypedFormControl(this.paymentData.paymentType, [
				Validators.required,
			]),
			paymentProvider: new UntypedFormControl(this.paymentData.paymentProvider, [
				Validators.required,
			]),
			paymentChannel: new UntypedFormControl(this.paymentData.paymentChannel, [
				Validators.required,
			]),
			paymentOptionFPType: new UntypedFormControl(this.paymentData.paymentProvider, [
				Validators.required,
			]),
			ewalletType: new UntypedFormControl(this.paymentData.ewalletType, []),
			fpxEnterpriseType: new UntypedFormControl(this.paymentData.fpxEnterpriseType, []),
			// For EPP only
			paymentOptionType: new UntypedFormControl(
				this.paymentData.paymentOptionType,
				[],
			),
			bank: new UntypedFormControl(this.paymentData.bank, []),
			installmentMonth: new UntypedFormControl(this.paymentData.installmentMonth, []),
		});

		let paymentFormVal = this.paymentFormGroup.controls;
		let paymentChannelControl = this.paymentFormGroup.controls.paymentChannel;
		let bankControl = this.paymentFormGroup.controls.bank;
		let ewalletTypeControl = this.paymentFormGroup.controls.ewalletType;
		let paymentTypeControl = this.paymentFormGroup.controls.paymentType;
		let installmentMonthControl =
			this.paymentFormGroup.controls.installmentMonth;

		// To compensate for https://policystreet.atlassian.net/browse/A2C-392. In backend Shopee provider is still RMS/e-wallet
		// Let's keep it this way to stay true to the data and allow easy revert.
		if (
			this.paymentFormGroup?.value?.paymentType ==
				this.paymentOptionFullPrice &&
			this.paymentFormGroup?.value?.paymentProvider ==
				this.paymentOptionRazer &&
			this.paymentFormGroup?.value?.ewalletType == this.shopeePay
		) {
			this.paymentFormGroup.patchValue({
				paymentOptionFPType: this.paymentOptionShopee,
			});
		}

		// To toggle e-wallet dropdown to be required/not
		paymentChannelControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((x) => {
				if (x == this.paymentOptionEwallet) {
					ewalletTypeControl?.setValidators([Validators.required]);
				} else {
					ewalletTypeControl?.setValidators([]);
				}
				ewalletTypeControl?.updateValueAndValidity();
				this.onPaymentFormValueChanges();
			});

		// To toggle other payment type child inputs to be required/not
		paymentTypeControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((x) => {
				this.paymentType = x;

				if (x == this.paymentOptionFullPrice) {
					paymentFormVal?.paymentType.setValidators([Validators.required]);
					paymentFormVal?.paymentProvider.setValidators([Validators.required]);
					paymentFormVal?.paymentChannel.setValidators([]);

					if (
						this.paymentFormGroup?.value?.paymentOptionFPType !=
						this.paymentOptionShopback
					)
						paymentFormVal?.paymentOptionFPType.setValidators([
							Validators.required,
						]);
					else paymentFormVal?.paymentOptionFPType.setValidators([]);

					// paymentFormVal.bank.reset();
					installmentMonthControl.reset();
					paymentFormVal?.installmentMonth.setValidators([]);
					paymentFormVal?.bank.setValidators([]);
				}

				if (x == this.paymentOptionEPP) {
					paymentFormVal?.bank.setValidators([Validators.required]);
					paymentFormVal?.installmentMonth.setValidators([Validators.required]);
					paymentFormVal?.paymentType.setValidators([Validators.required]);
					paymentFormVal?.paymentProvider.setValidators([Validators.required]);
					paymentFormVal?.paymentOptionFPType.setValidators([]);

					ewalletTypeControl.reset();
					paymentChannelControl.reset();
					installmentMonthControl.reset();

					this.setEppBanksList();
				}

				// if (x == this.paymentOptionShopback) {
				// 	paymentChannelControl.setValidators([]);
				// 	paymentFormVal?.bank.setValidators([]);
				// 	paymentFormVal?.installmentMonth.setValidators([]);
				// 	this.paymentFormGroup.patchValue({
				// 		installmentMonth: 3, //Shopback installment is 3 months
				// 		paymentProvider: this.paymentOptionShopback,
				// 	});

				// 	paymentFormVal.bank.reset();
				// 	ewalletTypeControl.reset();
				// }

				paymentFormVal?.paymentOptionFPType.updateValueAndValidity();
				paymentFormVal?.paymentOptionType.updateValueAndValidity();
				paymentFormVal?.paymentChannel.updateValueAndValidity();
				paymentFormVal?.paymentProvider.updateValueAndValidity();
				paymentFormVal?.bank.updateValueAndValidity();
				paymentFormVal?.installmentMonth.updateValueAndValidity();

				this.onPaymentFormValueChanges();
			});

		//When bank control changes. Reset installment month and set EPP rate options.
		bankControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((x) => {
			if (this.paymentFormGroup?.value?.paymentType == this.paymentOptionEPP) {
				// paymentFormVal.installmentMonth.setValue(0);
				this.installmentOptions = this.getInstallmentOptions(x);
				this.selectedEppRate = 0;
				this.paymentFormGroup.patchValue({
					paymentChannel: x,
				});

				paymentFormVal?.installmentMonth.updateValueAndValidity();
				paymentFormVal?.paymentChannel.updateValueAndValidity();
				this.onPaymentFormValueChanges();
			}
		});

		//When installment month control changes. Get the selected EPP rate
		installmentMonthControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((installmentMonth) => {
				//If all required EPP information is selected, get the respective EPP rate based on the bank and selected installment period
				if (
					bankControl.value &&
					installmentMonth !== null &&
					this.paymentFormGroup?.value?.paymentOptionType ==
						this.paymentOptionEPP3Or6Or12Months
				) {
					this.selectedEppRate = this.epp3Or6Or12MonthsBanks.find(
						(bank) =>
							bank.installmentPeriod == installmentMonth &&
							bank.bankName == bankControl.value,
					).eppRate;
				}

				if (
					bankControl.value &&
					installmentMonth !== null &&
					this.paymentFormGroup?.value?.paymentOptionType ==
						this.paymentOptionEPP6Or12Months
				) {
					this.selectedEppRate = this.epp6Or12MonthsBanks.find(
						(bank) =>
							bank.installmentPeriod == installmentMonth &&
							bank.bankName == bankControl.value,
					).eppRate;
				}

				this.onPaymentFormValueChanges();
			});
	}

	//This function returns the installment options for the selected bank
	private getInstallmentOptions(selectedBank: string) {
		let selectedBankEppOptions: any[] = [];

		//This function filters the list of EPP banks to only the selected one
		if (
			this.paymentFormGroup?.value?.paymentOptionType ==
			this.paymentOptionEPP3Or6Or12Months
		)
			selectedBankEppOptions = this.epp3Or6Or12MonthsBanks
				.filter(
					(bank) =>
						bank.bankName == selectedBank &&
						this.paymentValues.totalAmount >= bank.minAmount &&
						(bank.maxAmount == null ||
							this.paymentValues.totalAmount <= bank.maxAmount),
				)
				.sort(function (a, b) {
					var textA = a.installmentPeriod;
					var textB = b.installmentPeriod;
					return textA < textB ? -1 : textA > textB ? 1 : 0;
				});

		if (
			this.paymentFormGroup?.value?.paymentOptionType ==
			this.paymentOptionEPP6Or12Months
		)
			selectedBankEppOptions = this.epp6Or12MonthsBanks
				.filter(
					(bank) =>
						bank.bankName == selectedBank &&
						this.paymentValues.totalAmount >= bank.minAmount &&
						(bank.maxAmount == null ||
							this.paymentValues.totalAmount <= bank.maxAmount),
				)
				.sort(function (a, b) {
					var textA = a.installmentPeriod;
					var textB = b.installmentPeriod;
					return textA < textB ? -1 : textA > textB ? 1 : 0;
				});

		//Used to display the options in the dropdown. The mapping adds a new property called installmentAmount which calculates the monthly installmentAmount that is displayed in the dropdown options
		return selectedBankEppOptions.map((option) => {
			return {
				...option,
				installmentAmount:
					(option.eppRate * this.paymentValues.totalAmountBeforeServiceFee +
						this.paymentValues.totalAmount) /
					option.installmentPeriod,
			};
		});
	}

	//This function emits the user's selected payment method to the parent component on every form input change
	private onPaymentFormValueChanges() {
		this.paymentFormGroup.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((val: IPaymentData) => {
				this.selectedPaymentData.next({
					...val,
					formIsValid: this.paymentFormGroup.status === "VALID",
					eppRate: this.selectedEppRate,
				});
			});
	}

	//This function sets the bank dropdown values based on the banks that offers EPP
	setEppBanksList(promoCodeId?: string): Promise<void> {
		//This service calls an API to obtain the list of banks from the BE Database
		return new Promise((resolve, reject) => {
			try {
				let promoCode = promoCodeId || this.promo?.id;
				if(promoCodeId == '00000000-0000-0000-0000-000000000000')
				{
					promoCode = null;
				}

				this.orderDataService
					.getEppBankInstallmentList(this.request.dealerType, promoCode)
					.subscribe((banks) => {
						
						//Banks that are valid must have a channel defined
						const validBanks = banks.filter((bank) => bank.channel != "");

						//Set bank array for epp options
						this.epp3Or6Or12MonthsBanks = validBanks.filter(
							(epp) =>
								epp.provider === this.paymentOptionRazer &&
								this.paymentValues.totalAmount >= epp.minAmount &&
								(epp.maxAmount == null ||
									this.paymentValues.totalAmount <= epp.maxAmount),
						);

						this.epp6Or12MonthsBanks = validBanks.filter(
							(epp) =>
								epp.provider === this.paymentOptionPayex &&
								this.paymentValues.totalAmount >= epp.minAmount &&
								(epp.maxAmount == null ||
									this.paymentValues.totalAmount <= epp.maxAmount),
						);

						//Set epp listing for epp options without bank duplicate
						let epp3Or6Or12MonthsGroupByBankName =
							this.epp3Or6Or12MonthsBanks.reduce((group, eppCharge) => {
								const { bankName } = eppCharge;
								group[bankName] = group[bankName] ?? [];
								group[bankName].push(eppCharge);
								return group;
							}, {});
						this.epp3Or6Or12MonthsListing = Object.values(
							epp3Or6Or12MonthsGroupByBankName,
						).map((group) => group[0]);
						this.epp3Or6Or12MonthsListing = this.epp3Or6Or12MonthsListing.sort(
							function (a, b) {
								var textA = a.bankName.toUpperCase();
								var textB = b.bankName.toUpperCase();
								return textA < textB ? -1 : textA > textB ? 1 : 0;
							},
						);

						let epp6Or12MonthsGroupByBankName = this.epp6Or12MonthsBanks.reduce(
							(group, eppCharge) => {
								const { bankName } = eppCharge;
								group[bankName] = group[bankName] ?? [];
								group[bankName].push(eppCharge);
								return group;
							},
							{},
						);
						this.epp6Or12MonthsListing = Object.values(
							epp6Or12MonthsGroupByBankName,
						).map((group) => group[0]);
						this.epp6Or12MonthsListing = this.epp6Or12MonthsListing.sort(
							function (a, b) {
								var textA = a.bankName.toUpperCase();
								var textB = b.bankName.toUpperCase();
								return textA < textB ? -1 : textA > textB ? 1 : 0;
							},
						);

						//Set visibility of epp options based on total amount
						let epp3Or6Or12MonthsMinAmount = Math.min(
							...this.epp3Or6Or12MonthsBanks.map(
								(eppCharge) => eppCharge.minAmount,
							),
						);
						this.showEPP3Or6Or12Months =
							this.paymentValues.totalAmount >= epp3Or6Or12MonthsMinAmount;

						let epp6Or12MonthsMinAmount = Math.min(
							...this.epp6Or12MonthsBanks.map(
								(eppCharge) => eppCharge.minAmount,
							),
						);
						this.showEPP6Or12Months =
							this.paymentValues.totalAmount >= epp6Or12MonthsMinAmount;

						// Check if the component need to be pre-selected with draft data (Direct Lending)
						if (this.isApplyDraft) {
							this.installmentOptions = this.getInstallmentOptions(
								this.paymentData.bank,
							);
							this.paymentFormGroup.controls.installmentMonth.setValue(
								this.paymentData.installmentMonth,
							);
							this.paymentFormGroup.controls.installmentMonth.updateValueAndValidity();
						}

						resolve();
					});
			} catch (error) {
				reject(error);
			}
		});
	}

	ngOnDestroy() {
		this.paymentData.paymentType = "";
		this.paymentData.paymentProvider = "";
		this.paymentData.bank = "";
		this.paymentData.installmentMonth = null;
		this.paymentData.paymentOptionType = "";
		this.paymentData.paymentChannel = "";

		this.destroy$.next(null);
		this.destroy$.complete();
	}

	getPaymentMonthLabel(period: number) {
		if (period == 3) return "paymentOptions.3months";
		if (period == 6) return "paymentOptions.6months";
		if (period == 12) return "paymentOptions.12months";

		return "";
	}

	epp3Or6Or12MonthsSelected(event: MatRadioChange) {
		this.paymentFormGroup.patchValue({
			paymentProvider: this.paymentOptionRazer,
			bank: null,
			installmentMonth: null,
			paymentType: this.paymentOptionEPP,
		});
	}

	fpShopeeSelected(event: MatRadioChange) {
		this.paymentFormGroup.patchValue({
			//paymentChannel: this.shopeePay,
			ewalletType: this.shopeePay,
			paymentChannel: this.paymentOptionEwallet,
			paymentProvider: this.paymentOptionRazer,
			paymentType: this.paymentOptionFullPrice,
			paymentOptionFPType: this.paymentOptionShopee,
		});
	}

	fpShopbackSelected(event: MatRadioChange) {
		this.paymentFormGroup.patchValue({
			paymentChannel: this.paymentOptionShopback,
			paymentProvider: this.paymentOptionShopback,
			paymentType: this.paymentOptionFullPrice,
			paymentOptionFPType: this.paymentOptionShopback,
		});
	}

	fpAtomeSelected(event: MatRadioChange) {
		this.paymentFormGroup.patchValue({
			paymentChannel: this.paymentOptionAtome,
			paymentProvider: this.paymentOptionAtome,
			paymentType: this.paymentOptionBNPL
		});
	}

	fpRazerSelected(event: MatRadioChange) {
		this.paymentFormGroup.patchValue({
			paymentProvider: this.paymentOptionRazer,
			paymentType: this.paymentOptionFullPrice,
			paymentOptionFPType: this.paymentOptionRazer,
			ewalletType: null,
		});
	}

	epp6Or12MonthsSelected(event: MatRadioChange) {
		this.paymentFormGroup.patchValue({
			paymentProvider: this.paymentOptionPayex,
			bank: null,
			installmentMonth: null,
			paymentType: this.paymentOptionEPP,
		});
	}

	refreshEppPaymentOption()
	{
		this.paymentFormGroup.patchValue({
			paymentProvider: this.paymentData.paymentProvider,
			paymentType: this.paymentData.paymentType,
			bank: this.paymentData.bank,
			installmentMonth: this.paymentData.installmentMonth,
		});
	}

	get paymentTypeVal() {
		return this.paymentFormGroup?.value?.paymentType;
	}

	get paymentChannelVal() {
		return this.paymentFormGroup?.value?.paymentChannel;
	}

	get totalInstallmentAmount() {
		if (this.paymentType === this.paymentOptionShopback) {
			return this.paymentValues.totalAmount;
		}

		if (this.paymentType === this.paymentOptionEPP) {
			return this.paymentValues.bankInstallmentTotalAmount;
		}

		return this.paymentValues.totalAmount;
	}

	get paymentOptionFPTypeVal() {
		return this.paymentFormGroup?.value?.paymentOptionFPType;
	}
}
